import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type CashSettingsEntity } from '../../api.ts';
import { ListToolbar } from '../../components/ListFilters.tsx';
import {
  Badge,
  EmptyRow,
  ErrorBox,
  Loading,
  NumCell,
  Panel,
  Select,
  StatCard,
  Table,
} from '../../components/ui.tsx';
import { useFormat } from '../purchasing/common.tsx';
import { BarChart, LineChart } from './TreasuryCharts.tsx';

const KEY = ['finance', 'treasury'];

interface Overview {
  today: string;
  indicators: {
    confirmed: { currency: string; amount: string | null }[];
    projected7: { currency: string; closing: string }[];
    projected30: { currency: string; closing: string }[];
    projected90: { currency: string; closing: string }[];
    next30: { currency: string; inflows: string; outflows: string }[];
    minimum: { currency: string; amount: string };
    overdue: { currency: string; amount: string }[];
    budgetConnected: boolean;
    outsideBudget: number;
  };
  realized: { date: string; currency: string; closing: string }[];
  projected: { date: string; currency: string; closing: string }[];
  alerts: {
    kind: string;
    refKey: string;
    refDate: string | null;
    currency: string | null;
    amount: string | null;
    link: string;
    details: Record<string, unknown>;
  }[];
}

export interface ReportTable {
  report: string;
  columns: string[];
  rows: Record<string, string | number | boolean | null>[];
  notes: string[];
}

/** Escolha da empresa, igual nas abas da Tesouraria (empresas com o caixa configurado). */
export function useEntityPicker(): {
  entity: CashSettingsEntity | null;
  picker: ReactNode;
  state: ReactNode;
} {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState('');
  const settings = useQuery({
    queryKey: [...KEY, 'projection', 'settings'],
    queryFn: () => api.get<{ entities: CashSettingsEntity[] }>('/finance/treasury/settings'),
  });
  if (settings.isPending) return { entity: null, picker: null, state: <Loading /> };
  if (settings.isError) {
    return { entity: null, picker: null, state: <ErrorBox error={settings.error} /> };
  }
  const entities = settings.data.entities.filter((e) => e.settings);
  if (entities.length === 0) {
    return {
      entity: null,
      picker: null,
      state: (
        <Panel>
          <p className="text-sm text-muted">{t('finance.flow.noEntities')}</p>
        </Panel>
      ),
    };
  }
  const entity = entities.find((e) => e.legalEntityId === entityId) ?? entities[0]!;
  return {
    entity,
    state: null,
    picker: (
      <Select
        id="treasury-entity"
        aria-label={t('finance.flow.fields.entity')}
        value={entity.legalEntityId}
        onChange={(e) => setEntityId(e.target.value)}
      >
        {entities.map((e) => (
          <option key={e.legalEntityId} value={e.legalEntityId}>
            {e.code} · {e.name}
          </option>
        ))}
      </Select>
    ),
  };
}

const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const num = (v: string | null | undefined) => (v === null || v === undefined ? null : Number(v));

/** Visão geral (§13): indicadores, alertas informativos com link e a evolução do caixa. */
export function TreasuryOverview() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { entity, picker, state } = useEntityPicker();
  const stats = useQuery({
    queryKey: [...KEY, 'statistics', entity?.legalEntityId],
    queryFn: () =>
      api.get<
        | { available: false; months: number; required: number }
        | {
            available: true;
            months: number;
            inflows: string;
            outflows: string;
            net: string;
            netDeviation: string;
          }
      >(`/finance/treasury/statistics?legalEntityId=${entity!.legalEntityId}`),
    enabled: !!entity,
  });
  const data = useQuery({
    queryKey: [...KEY, 'overview', entity?.legalEntityId],
    queryFn: () =>
      api.get<Overview>(`/finance/treasury/overview?legalEntityId=${entity!.legalEntityId}`),
    enabled: !!entity,
  });
  if (state) return state;
  const o = data.data;
  const money = (v: string | null | undefined, c: string) =>
    v === null || v === undefined ? '—' : fmt.money(v, c);
  const currencies = o ? [...new Set(o.projected.map((p) => p.currency))] : [];
  return (
    <div className="flex flex-col gap-6">
      <ListToolbar>{picker}</ListToolbar>
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {o && (
        <>
          {o.indicators.confirmed.map((c) => {
            const at = (list: { currency: string; closing: string }[]) =>
              list.find((x) => x.currency === c.currency)?.closing ?? null;
            const flow = o.indicators.next30.find((x) => x.currency === c.currency);
            const overdue = o.indicators.overdue.find((x) => x.currency === c.currency);
            return (
              <div key={c.currency} className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatCard
                  label={t('finance.overview.confirmed', { currency: c.currency })}
                  value={money(c.amount, c.currency)}
                />
                <StatCard
                  label={t('finance.overview.projected', { days: 7 })}
                  value={money(c.amount === null ? null : at(o.indicators.projected7), c.currency)}
                />
                <StatCard
                  label={t('finance.overview.projected', { days: 30 })}
                  value={money(c.amount === null ? null : at(o.indicators.projected30), c.currency)}
                />
                <StatCard
                  label={t('finance.overview.projected', { days: 90 })}
                  value={money(c.amount === null ? null : at(o.indicators.projected90), c.currency)}
                />
                <StatCard
                  label={t('finance.overview.inflows30')}
                  value={money(flow?.inflows, c.currency)}
                />
                <StatCard
                  label={t('finance.overview.outflows30')}
                  value={money(flow?.outflows, c.currency)}
                />
                <StatCard
                  label={t('finance.overview.overdue')}
                  value={money(overdue?.amount, c.currency)}
                  alert={!!overdue && overdue.amount !== '0.0000'}
                />
                <StatCard
                  label={t('finance.overview.minimum')}
                  value={money(o.indicators.minimum.amount, o.indicators.minimum.currency)}
                />
              </div>
            );
          })}
          <p className="text-sm text-muted">
            {o.indicators.budgetConnected
              ? t('finance.overview.outsideBudget', { n: o.indicators.outsideBudget })
              : t('finance.budget.pending')}
          </p>
          {stats.data && (
            <p className="text-sm text-muted">
              {stats.data.available
                ? t('finance.overview.statistics', {
                    inflows: numberFormat.format(Number(stats.data.inflows)),
                    outflows: numberFormat.format(Number(stats.data.outflows)),
                    deviation: numberFormat.format(Number(stats.data.netDeviation)),
                  })
                : t('finance.overview.statisticsUnavailable', {
                    months: stats.data.months,
                    required: stats.data.required,
                  })}
            </p>
          )}
          <Panel title={t('finance.overview.alerts')}>
            <Table
              head={[
                t('finance.overview.alert'),
                t('finance.cash.fields.date'),
                { label: t('finance.cash.fields.amount'), align: 'right' },
                '',
              ]}
            >
              {o.alerts.length === 0 && (
                <EmptyRow colSpan={4}>{t('finance.overview.noAlerts')}</EmptyRow>
              )}
              {o.alerts.map((a) => (
                <tr key={`${a.kind}:${a.refKey}`}>
                  <td>
                    <Badge
                      tone={
                        a.kind === 'negative_balance' || a.kind === 'below_minimum' ? 'off' : 'info'
                      }
                    >
                      {t(`finance.overview.alertKinds.${a.kind}`)}
                    </Badge>
                    <div className="text-xs text-muted">
                      {String(a.details.label ?? a.details.description ?? a.details.account ?? '')}
                    </div>
                  </td>
                  <td className="whitespace-nowrap">{a.refDate ? fmt.date(a.refDate) : '—'}</td>
                  <NumCell>
                    {a.amount && a.currency ? fmt.money(a.amount, a.currency) : '—'}
                  </NumCell>
                  <td className="text-right">
                    <a className="underline" href={a.link}>
                      {t('finance.overview.open')}
                    </a>
                  </td>
                </tr>
              ))}
            </Table>
          </Panel>
          {currencies.map((c) => {
            const realized = o.realized
              .filter((r) => r.currency === c)
              .sort((a, b) => a.date.localeCompare(b.date));
            const projected = o.projected.filter((r) => r.currency === c);
            const labels = [...realized.map((r) => r.date), ...projected.map((r) => r.date)];
            return (
              <Panel key={c}>
                <LineChart
                  title={t('finance.overview.evolution', { currency: c })}
                  labels={labels.map((d) => fmt.date(d))}
                  marker={{ index: realized.length, label: t('finance.overview.today') }}
                  format={(v) => fmt.money(String(v.toFixed(2)), c)}
                  series={[
                    {
                      name: t('finance.overview.realized'),
                      values: [
                        ...realized.map((r) => num(r.closing)),
                        ...projected.map(() => null),
                      ],
                    },
                    {
                      name: t('finance.overview.projectedSeries'),
                      values: [
                        ...realized.map(() => null),
                        ...projected.map((r) => num(r.closing)),
                      ],
                    },
                  ]}
                />
              </Panel>
            );
          })}
        </>
      )}
    </div>
  );
}

const MONTHLY = ['payables', 'forecastIn', 'forecastOut', 'payroll', 'receipts', 'budget'] as const;

/**
 * Fluxo mensal (§13): meses nas colunas e categorias nas linhas; o saldo final de um mês é o
 * inicial do seguinte, na moeda escolhida. Gráficos: entradas e saídas e a composição das saídas.
 */
export function TreasuryMonthly() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { entity, picker, state } = useEntityPicker();
  const [currency, setCurrency] = useState('');
  const data = useQuery({
    queryKey: [...KEY, 'report', 'monthly', entity?.legalEntityId],
    queryFn: () =>
      api.get<ReportTable>(
        `/finance/treasury/reports/monthly?legalEntityId=${entity!.legalEntityId}&days=365`,
      ),
    enabled: !!entity,
  });
  if (state) return state;
  const rows = data.data?.rows ?? [];
  const currencies = [...new Set(rows.map((r) => String(r.currency)))];
  const cur = currencies.includes(currency) ? currency : (currencies[0] ?? '');
  const mine = rows.filter((r) => r.currency === cur);
  const money = (v: unknown) => (v === null || v === undefined ? '—' : fmt.money(String(v), cur));
  const months = mine.map((r) => String(r.month).slice(0, 7));
  const value = (r: (typeof mine)[number], k: string) => Number(r[k] ?? 0);
  return (
    <div className="flex flex-col gap-6">
      <ListToolbar>
        {picker}
        {currencies.length > 1 && (
          <Select
            id="monthly-currency"
            aria-label={t('finance.flow.fields.currency')}
            value={cur}
            onChange={(e) => setCurrency(e.target.value)}
          >
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        )}
      </ListToolbar>
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.isSuccess && (
        <>
          <Panel title={t('finance.monthly.title', { currency: cur })}>
            <div className="overflow-x-auto">
              <Table
                head={[
                  t('finance.monthly.category'),
                  ...months.map((m) => ({ label: fmt.month(m), align: 'right' as const })),
                ]}
              >
                <tr>
                  <td className="font-medium">{t('finance.monthly.opening')}</td>
                  {mine.map((r) => (
                    <NumCell key={String(r.month)}>{money(r.opening)}</NumCell>
                  ))}
                </tr>
                {MONTHLY.map((cat) => (
                  <tr key={cat}>
                    <td>{t(`finance.monthly.categories.${cat}`)}</td>
                    {mine.map((r) => (
                      <NumCell key={String(r.month)}>{money(r[cat])}</NumCell>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="font-medium">{t('finance.monthly.closing')}</td>
                  {mine.map((r) => (
                    <NumCell key={String(r.month)}>{money(r.closing)}</NumCell>
                  ))}
                </tr>
              </Table>
            </div>
          </Panel>
          <Panel>
            <BarChart
              title={t('finance.monthly.inOut')}
              labels={months}
              format={(v) => fmt.money(v.toFixed(2), cur)}
              series={[
                {
                  name: t('finance.monthly.inflows'),
                  values: mine.map((r) => value(r, 'forecastIn') + value(r, 'receipts')),
                },
                {
                  name: t('finance.monthly.outflows'),
                  values: mine.map(
                    (r) =>
                      -(
                        value(r, 'payables') +
                        value(r, 'forecastOut') +
                        value(r, 'payroll') +
                        value(r, 'budget')
                      ),
                  ),
                },
              ]}
            />
          </Panel>
          <Panel>
            <BarChart
              stacked
              title={t('finance.monthly.composition')}
              labels={months}
              format={(v) => fmt.money(v.toFixed(2), cur)}
              series={(['payables', 'forecastOut', 'payroll', 'budget'] as const).map((cat) => ({
                name: t(`finance.monthly.categories.${cat}`),
                values: mine.map((r) => -value(r, cat)),
              }))}
            />
          </Panel>
        </>
      )}
    </div>
  );
}
